import { ControllerParams, IHttpClient } from '@wix/yoshi-flow-editor';
import type { ErrorHandler } from '@wix/error-handler';
import { makeAutoObservable } from 'mobx';
import { GiftCardProduct } from '../../../../types';
import {
  createGiftCardProduct,
  queryGiftCardProducts,
} from '@wix/ambassador-gift-cards-v1-gift-card-product/http';
import {
  mapGiftCardProductV1ToGiftCardProduct,
  mapGiftCardProductToGiftCardProductV1,
} from '../utils/giftCardProductMapper';
import { PanoramaTransactions, SPECS } from '../consts';
import staticDefaultTemplate from './staticDefaultTemplate';

export class GiftCardService {
  httpClient: IHttpClient;
  instance: string;
  panoramaClient: ControllerParams['flowAPI']['panoramaClient'];
  translate: ControllerParams['flowAPI']['translations']['t'];
  experiments: ControllerParams['flowAPI']['experiments'];
  withErrorHandler?: ErrorHandler['withErrorHandler'];
  getResolvedError?: ErrorHandler['getResolvedError'];

  constructor(
    controllerParams: ControllerParams,
    options?: {
      withErrorHandler?: ErrorHandler['withErrorHandler'];
      getResolvedError?: ErrorHandler['getResolvedError'];
    },
  ) {
    makeAutoObservable(this);
    this.translate = controllerParams.flowAPI.translations.t;
    this.httpClient = controllerParams.flowAPI.httpClient;
    this.instance = controllerParams.controllerConfig.appParams.instance;
    this.panoramaClient = controllerParams.flowAPI.panoramaClient;
    this.experiments = controllerParams.flowAPI.experiments;
    this.withErrorHandler = options?.withErrorHandler;
    this.getResolvedError = options?.getResolvedError;
  }

  async getGiftCardProductV1({ currency }: { currency?: string }) {
    try {
      this.panoramaClient
        ?.transaction(PanoramaTransactions.giftCardProductsExist)
        .start();
      const shouldAddWixCurrencyHeader = this.experiments.enabled(
        SPECS.AddWixCurrencyHeader,
      );
      const shouldUseErrorHandler = this.experiments.enabled(
        SPECS.GiftCardShouldUseErrorHandler,
      );

      const requestFn = () =>
        this.httpClient.request((args) => ({
          ...queryGiftCardProducts({})(args),
          ...(shouldAddWixCurrencyHeader && currency
            ? { headers: { 'x-wix-currency': currency } }
            : {}),
        }));

      const response =
        shouldUseErrorHandler && this.withErrorHandler
          ? await this.withErrorHandler(requestFn, { errorCodesMap: {} })
          : await requestFn();

      const {
        data: { giftCardProducts },
      } = response;

      const product = giftCardProducts?.[0];

      if (product) {
        this.panoramaClient
          ?.transaction(PanoramaTransactions.giftCardProductsExist)
          .finish();
      }

      return (
        product &&
        mapGiftCardProductV1ToGiftCardProduct(
          this.instance,
          product,
          shouldAddWixCurrencyHeader,
        )
      );
    } catch (error) {
      if (this.getResolvedError) {
        this.getResolvedError(error);
      }
      this.panoramaClient?.errorMonitor().reportError(error as Error);
      console.error(
        'An error occurred while fetching Gift Card product:',
        error,
      );
    }
  }

  async getDefaultTemplate() {
    const nameDefaultKey = 'giftCards.editGiftCard.infoCard.nameDefault';
    const descriptionDefaultKey =
      'giftCards.editGiftCard.infoCard.descriptionDefault';

    return {
      ...staticDefaultTemplate,
      name: this.translate(nameDefaultKey),
      description: this.translate(descriptionDefaultKey),
    } as GiftCardProduct;
  }

  async createGiftCardProductV1() {
    this.panoramaClient
      ?.transaction(PanoramaTransactions.giftCardCreateDefaultProduct)
      .start();
    const productData = mapGiftCardProductToGiftCardProductV1(
      await this.getDefaultTemplate(),
    );

    const shouldUseErrorHandler = this.experiments.enabled(
      SPECS.GiftCardShouldUseErrorHandler,
    );

    const requestFn = () =>
      this.httpClient.request(
        createGiftCardProduct({ giftCardProduct: productData }),
      );

    const response =
      shouldUseErrorHandler && this.withErrorHandler
        ? await this.withErrorHandler(requestFn, { errorCodesMap: {} as any })
        : await requestFn();

    const {
      data: { giftCardProduct },
    } = response;

    this.panoramaClient
      ?.transaction(PanoramaTransactions.giftCardCreateDefaultProduct)
      .finish();
    return giftCardProduct;
  }

  async getOrCreateGiftCardProductV1({ currency }: { currency?: string }) {
    let product: any;
    try {
      const shouldAddWixCurrencyHeader = this.experiments.enabled(
        SPECS.AddWixCurrencyHeader,
      );
      const shouldUseErrorHandler = this.experiments.enabled(
        SPECS.GiftCardShouldUseErrorHandler,
      );

      const requestFn = () =>
        this.httpClient.request((args) => ({
          ...queryGiftCardProducts({})(args),
          ...(shouldAddWixCurrencyHeader && currency
            ? { headers: { 'x-wix-currency': currency } }
            : {}),
        }));

      const response =
        shouldUseErrorHandler && this.withErrorHandler
          ? await this.withErrorHandler(requestFn, { errorCodesMap: {} })
          : await requestFn();

      const {
        data: { giftCardProducts },
      } = response;

      product = giftCardProducts?.[0];

      if (!product) {
        product = await this.createGiftCardProductV1();
      }

      return (
        product &&
        mapGiftCardProductV1ToGiftCardProduct(
          this.instance,
          product,
          shouldAddWixCurrencyHeader,
        )
      );
    } catch (error) {
      if (this.getResolvedError) {
        this.getResolvedError(error);
      }
      this.panoramaClient?.errorMonitor().reportError(error as Error);
      console.error(
        'An error occurred while fetching Gift Card product:',
        error,
      );
    }
  }

  async getCurrencyCode(): Promise<string> {
    const getCurrencyCodeRequest = await this.httpClient.request<string>({
      url: 'https://www.wix.com/_serverless/rise-proxy/wix',
      method: 'post',
      headers: {
        Authorization: this.instance,
      },
      disableWixHeaders: true,
      data: {
        wixRequest: {
          serviceRequest: {
            entity: 'SettingsReaderService',
            method: 'getCurrency',
            params: {},
          },
        },
      },
    });

    return getCurrencyCodeRequest.data;
  }
}
